import Flex from "../../components/Flex";
import { formatDateTime, formatTime } from "../../utils/date-time-utility";
import PsychoeducationSection from "../_shared/PsychoeducationSection";
import {
  BillingBlock,
  NestedText,
  RiskBlock,
  SubSection,
  labelList,
  splitCsv,
  THOUGHT_CONTENT_LABELS,
} from "../_shared/TherapyBlocks";
import AllergiesBlock from "../_shared/AllergiesBlock";
import HistoryBlock from "../_shared/HistoryBlock";
import MedicationsBlock from "../_shared/MedicationsBlock";
import VitalsBlock from "../_shared/VitalsBlock";
import RosBlock from "../_shared/RosBlock";
import PhysicalExamBlock from "../_shared/PhysicalExamBlock";
import ScreeningAssessmentBlock from "../_shared/ScreeningAssessmentBlock";
import AssessmentBlock from "../_shared/AssessmentBlock";
import PrescriptionsBlock from "../_shared/PrescriptionsBlock";
import VaccinesBlock from "../_shared/VaccinesBlock";
import CptCodesBlock from "../_shared/CptCodesBlock";
import {
  CarePlanTemplateList,
  KeyValPair,
  OrderList,
  RenderTextWithContainerPattern,
  ReportSection,
  checkNull,
} from "../constant";

interface IndividualTherapyNoteTemplateProps {
  data: any;
}

const COORDINATION_LABELS: Record<string, string> = {
  contactPsychiatrist: "Contact psychiatrist re: medication",
  sendUpdateToPCP: "Send update to PCP",
  caseManagementReferral: "Case management referral",
};

const IndividualTherapyNoteTemplate = ({
  data,
}: IndividualTherapyNoteTemplateProps) => {
  const thoughtContent = splitCsv(data?.thoughtContent);
  const interventions = splitCsv(data?.selectedInterventions);
  const coordination = splitCsv(data?.coordinationNeeded);

  const hasSessionDetails =
    data?.presentingConcernsToday ||
    data?.sessionType ||
    data?.patientEngagement !== undefined;

  const hasMse =
    data?.appearance ||
    data?.behavior ||
    data?.speech ||
    data?.mood ||
    data?.affect ||
    data?.thoughtProcess ||
    checkNull(thoughtContent) ||
    data?.cognition ||
    data?.insight ||
    data?.insightJudgment;

  const hasGuardian =
    data?.guardianParentName || data?.relationship || data?.mobileNumber;

  const hasInterventions =
    checkNull(interventions) || data?.sessionFocusContent;

  const hasPlan =
    data?.clinicalImpressions ||
    data?.planForNextSession ||
    data?.nextAppointmentDate ||
    data?.nextAppointmentTime ||
    data?.sessionFrequency ||
    checkNull(coordination);

  return (
    <>
      {/* ── Subjective ── */}
      <ReportSection label="Subjective">
        <RenderTextWithContainerPattern
          label="History of Present Illness"
          val={data?.historyOfPresentIllness}
        />
        <RenderTextWithContainerPattern
          label="Subjective"
          val={data?.subjectiveFreeNote}
        />
        <AllergiesBlock data={data} />
        <HistoryBlock data={data} />
        <RenderTextWithContainerPattern label="Habits" val={data?.habits} />
        <MedicationsBlock data={data} />
      </ReportSection>

      {/* ── Objective ── */}
      <ReportSection label="Objective">
        <RenderTextWithContainerPattern
          label="Objective"
          val={data?.objectiveFreeNote}
        />
        <VitalsBlock data={data} />
        <PhysicalExamBlock data={data} />
        <RosBlock data={data} />
        <ScreeningAssessmentBlock data={data} />
      </ReportSection>

      {/* ── Assessment ── */}
      <ReportSection label="Assessment">
        <AssessmentBlock data={data} />
      </ReportSection>

      {/* ── Plan ── */}
      <ReportSection label="Plan">
        <RenderTextWithContainerPattern label="Plan" val={data?.planFreeNote} />
        <PrescriptionsBlock data={data} />
        <VaccinesBlock data={data} />
        <OrderList orders={data?.patientOrders} />
        <CptCodesBlock data={data} />
        <RenderTextWithContainerPattern
          label="Care Plan"
          val={data?.carePlan}
        />
        <CarePlanTemplateList carePlanTemplates={data?.carePlanTemplates} />
        <RenderTextWithContainerPattern
          label="Instructions"
          val={data?.instruction}
        />
      </ReportSection>

      {/* ── Individual Therapy Note Specific ── */}
      <ReportSection label="Individual Therapy Note">
        <Flex flexDirection={"column"} gap={1} mt={1} p={1}>
          {hasSessionDetails && (
            <SubSection label="Session Details">
              <NestedText
                label="Presenting Concerns Today"
                val={data?.presentingConcernsToday}
              />
              {data?.sessionType && (
                <KeyValPair k="Session Type" v={data.sessionType} />
              )}
              {data?.patientEngagement !== undefined &&
                data?.patientEngagement !== null &&
                data?.patientEngagement !== 0 && (
                  <KeyValPair
                    k="Patient Engagement"
                    v={`${data.patientEngagement}/5`}
                  />
                )}
            </SubSection>
          )}

          {hasMse && (
            <SubSection label="Clinical Assessment / Mental Status Examination">
              {data?.appearance && (
                <KeyValPair k="Appearance" v={data.appearance} />
              )}
              {data?.behavior && <KeyValPair k="Behavior" v={data.behavior} />}
              {data?.speech && <KeyValPair k="Speech" v={data.speech} />}
              {data?.mood && <KeyValPair k="Mood (stated)" v={data.mood} />}
              {data?.affect && (
                <KeyValPair k="Affect (observed)" v={data.affect} />
              )}
              {data?.thoughtProcess && (
                <KeyValPair k="Thought Process" v={data.thoughtProcess} />
              )}
              {checkNull(thoughtContent) && (
                <KeyValPair
                  k="Thought Content"
                  v={labelList(thoughtContent, THOUGHT_CONTENT_LABELS)}
                />
              )}
              {data?.cognition && (
                <KeyValPair k="Cognition" v={data.cognition} />
              )}
              {data?.insight && <KeyValPair k="Insight" v={data.insight} />}
              {data?.insightJudgment && (
                <KeyValPair k="Judgement" v={data.insightJudgment} />
              )}
            </SubSection>
          )}

          {hasGuardian && (
            <SubSection label="Guardian Information">
              {data?.guardianParentName && (
                <KeyValPair
                  k="Guardian/Parent Name"
                  v={data.guardianParentName}
                />
              )}
              {data?.relationship && (
                <KeyValPair k="Relationship" v={data.relationship} />
              )}
              {data?.mobileNumber && (
                <KeyValPair k="Mobile Number" v={data.mobileNumber} />
              )}
            </SubSection>
          )}

          <RiskBlock data={data} />

          {hasInterventions && (
            <SubSection label="Interventions & Therapeutic Work">
              {checkNull(interventions) && (
                <KeyValPair
                  k="Interventions Selected"
                  v={interventions.join(", ")}
                />
              )}
              <NestedText
                label="Session Focus & Content"
                val={data?.sessionFocusContent}
              />
            </SubSection>
          )}

          {data?.patientResponseToTreatment && (
            <SubSection label="Patient Response to Treatment">
              <NestedText
                label="Patient Response to Treatment"
                val={data?.patientResponseToTreatment}
              />
            </SubSection>
          )}

          {data?.treatmentGoalsProgress && (
            <SubSection label="Treatment Goals Progress">
              <NestedText
                label="Treatment Goals Progress"
                val={data?.treatmentGoalsProgress}
              />
            </SubSection>
          )}

          {hasPlan && (
            <SubSection label="Plan & Next Steps">
              <NestedText
                label="Clinical Impressions"
                val={data?.clinicalImpressions}
              />
              <NestedText
                label="Plan for Next Session"
                val={data?.planForNextSession}
              />
              {data?.nextAppointmentDate && (
                <KeyValPair
                  k="Next Appointment Date"
                  v={formatDateTime(data.nextAppointmentDate) ?? "-"}
                />
              )}
              {data?.nextAppointmentTime && (
                <KeyValPair
                  k="Next Appointment Time"
                  v={formatTime(data.nextAppointmentTime) ?? "-"}
                />
              )}
              {data?.sessionFrequency && (
                <KeyValPair k="Session Frequency" v={data.sessionFrequency} />
              )}
              {checkNull(coordination) && (
                <KeyValPair
                  k="Coordination Needed"
                  v={labelList(coordination, COORDINATION_LABELS)}
                />
              )}
            </SubSection>
          )}

          <BillingBlock data={data} />
        </Flex>
      </ReportSection>

      {/* ── Follow-Up ── */}
      <ReportSection label="Follow-Up">
        <RenderTextWithContainerPattern
          label="Follow Up"
          val={data?.followUp}
        />
      </ReportSection>

      {/* ── Psychoeducation Session (shown below Follow Up only when required) ── */}
      <ReportSection label="Psychoeducation Session">
        <PsychoeducationSection data={data} />
      </ReportSection>
    </>
  );
};

export default IndividualTherapyNoteTemplate;
